/**
 * LayoutNameModal Component
 *
 * Reusable name input modal for layout create and duplicate flows.
 * Pre-fills with optional default name, validates non-empty.
 *
 * Follows StrategyRenameModal pattern: local state, reset on open,
 * validation clears on change, toast on error.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useToastNotification,
} from '@enfomentis/ui';

// ===== TYPES =====

export interface LayoutNameModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback with validated name on confirm */
  onConfirm: (name: string) => Promise<void>;
  /** Modal title (e.g., "Create Layout", "Duplicate Layout") */
  title: string;
  /** Optional pre-filled name (e.g., "Copy of My Layout") */
  defaultName?: string;
  /** Confirm button label (defaults to "Create") */
  confirmLabel?: string;
}

// ===== COMPONENT =====

export const LayoutNameModal: React.FC<LayoutNameModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  defaultName,
  confirmLabel,
}) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useToastNotification();

  // Reset form when modal opens or defaultName changes
  useEffect(() => {
    if (isOpen) {
      setName(defaultName ?? '');
      setNameError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, defaultName]);

  const handleNameChange = (value: string): void => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(trimmedName);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" placement="top-center">
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <Input
            label="Name"
            placeholder="Enter layout name"
            value={name}
            onValueChange={handleNameChange}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
            {confirmLabel ?? 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
